import { useEffect, useState } from 'react';
import type { Customer, LogEntry, LogType, Role, User } from '../types';

interface ModalShellProps {
  children: React.ReactNode;
  onClose: () => void;
}

function ModalShell({ children, onClose }: ModalShellProps) {
  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
}

const TYPE_LABELS: Record<LogType, string> = { call: 'Call', email: 'Email', note: 'Note' };

interface LogModalProps {
  type: LogType;
  customers: Customer[];
  defaultCustomerId: string | null;
  onCancel: () => void;
  onSave: (customerId: string, notes: string) => void;
}

export function LogModal({ type, customers, defaultCustomerId, onCancel, onSave }: LogModalProps) {
  const [customerId, setCustomerId] = useState(defaultCustomerId ?? customers[0]?.id ?? '');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!customerId && customers[0]) setCustomerId(customers[0].id);
  }, [customers, customerId]);

  return (
    <ModalShell onClose={onCancel}>
      <div className="modal-title">New <span>{TYPE_LABELS[type]}</span></div>
      <div className="modal-field">
        <label>Customer</label>
        <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="modal-field">
        <label>Notes / Details</label>
        <textarea
          placeholder="Describe the call, email, or note…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      <div className="modal-btns">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => onSave(customerId, notes)}>Save Entry</button>
      </div>
    </ModalShell>
  );
}

interface CustomerModalProps {
  role: Role;
  users: User[];
  currentUserName: string;
  onCancel: () => void;
  onSave: (data: { name: string; amName: string; contact: string; phone: string; email: string }) => void;
}

export function CustomerModal({ role, users, currentUserName, onCancel, onSave }: CustomerModalProps) {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [amName, setAmName] = useState(role === 'admin' ? (users[0]?.name ?? '') : currentUserName);

  return (
    <ModalShell onClose={onCancel}>
      <div className="modal-title">Add <span>Customer</span></div>
      <div className="modal-field">
        <label>Customer / Company Name</label>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Ltd" />
      </div>
      <div className="modal-field">
        <label>Contact Person</label>
        <input type="text" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="e.g. Jane Smith" />
      </div>
      <div className="modal-field">
        <label>Phone</label>
        <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+44 …" />
      </div>
      <div className="modal-field">
        <label>Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
      </div>
      {role === 'admin' && (
        <div className="modal-field">
          <label>Account Manager</label>
          <select value={amName} onChange={(e) => setAmName(e.target.value)}>
            {users.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
          </select>
        </div>
      )}
      <div className="modal-btns">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => onSave({ name, amName, contact, phone, email })}>Add Customer</button>
      </div>
    </ModalShell>
  );
}

interface UserModalProps {
  onCancel: () => void;
  onSave: (data: { name: string; username: string; email: string; role: 'am' | 'jam'; password: string }) => void;
}

export function UserModal({ onCancel, onSave }: UserModalProps) {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'am' | 'jam'>('am');
  const [password, setPassword] = useState('');

  return (
    <ModalShell onClose={onCancel}>
      <div className="modal-title">Add <span>User</span></div>
      <div className="modal-field"><label>Full Name</label><input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" /></div>
      <div className="modal-field"><label>Username</label><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="jane.smith" /></div>
      <div className="modal-field"><label>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@printvision.com" /></div>
      <div className="modal-field">
        <label>Role</label>
        <select value={role} onChange={(e) => setRole(e.target.value as 'am' | 'jam')}>
          <option value="am">Account Manager</option>
          <option value="jam">Junior Account Manager</option>
        </select>
      </div>
      <div className="modal-field"><label>Password</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" /></div>
      <div className="modal-btns">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => onSave({ name, username, email, role, password })}>Add User</button>
      </div>
    </ModalShell>
  );
}

interface LogEditModalProps {
  log: LogEntry;
  onCancel: () => void;
  onSave: (patch: { type: LogType; notes: string }) => void;
}

export function LogEditModal({ log, onCancel, onSave }: LogEditModalProps) {
  const [type, setType] = useState<LogType>(log.type);
  const [notes, setNotes] = useState(log.notes);
  return (
    <ModalShell onClose={onCancel}>
      <div className="modal-title">Edit <span>{TYPE_LABELS[log.type]}</span></div>
      <div className="modal-field">
        <label>Type</label>
        <select value={type} onChange={(e) => setType(e.target.value as LogType)}>
          <option value="call">📞 Call</option>
          <option value="email">✉️ Email</option>
          <option value="note">📝 Note</option>
        </select>
      </div>
      <div className="modal-field">
        <label>Notes / Details</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div className="modal-btns">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-save" onClick={() => onSave({ type, notes: notes.trim() })}>Save Changes</button>
      </div>
    </ModalShell>
  );
}

interface ConfirmModalProps {
  title: string;
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmModal({ title, message, onCancel, onConfirm }: ConfirmModalProps) {
  return (
    <ModalShell onClose={onCancel}>
      <div className="modal-title danger">{title}</div>
      <p className="modal-warning">{message}</p>
      <div className="modal-btns">
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
        <button className="btn-danger" onClick={onConfirm}>Delete</button>
      </div>
    </ModalShell>
  );
}
